import { Injectable, inject } from '@angular/core';
import { CheckbillReqOtpRes, CheckbillRes } from '@entities/domains/checkbill.entity';
import { CreditList } from '@entities/domains/credit-list.entity';
import { ExpressPurchaseAssetConfig } from '@entities/domains/express-purchase-asset-config.entity';
import { FilterSections } from '@entities/domains/filter.entity';
import { Categories } from '@entities/domains/get-categories.entity';
import { FaqPackage, FaqPayload } from '@entities/domains/get-faq-package.entity';
import { OfferBreakConfig } from '@entities/domains/get-offer-break-config.entity';
import { PackageBySubCategory } from '@entities/domains/get-package-by-sub-category.entity';
import { PackageList } from '@entities/domains/get-package-list.entity';
import { SubCategories } from '@entities/domains/get-sub-categories.entity';
import { SubscriberType } from '@entities/domains/get-subscriber-type.entity';
import { Regions, RoamingType } from '@entities/domains/roaming.entity';
import { CheckbillAdditionalHeaderItem, CheckbillDto, CheckbillVerOtpDto } from '@entities/dtos/checkbill.interface';
import { GetCategoriesDTO } from '@entities/dtos/get-categories.interface';
import { GetPackageBySubCategoryDTO } from '@entities/dtos/get-package-by-sub-category.interface';
import { GetPackageListDTO } from '@entities/dtos/get-package-list.interface';
import { GetSubCategoriesDTO } from '@entities/dtos/get-sub-categories.interface';
import { GetSubscriberTypeDTO } from '@entities/dtos/get-subscriber-type.interface';
import { environment } from '@environment/environment';
import { ExpressPurchaseRepository } from '@repositories/express-purchase.repository';
import { RestApiService } from '@services/rest-api/rest-api.service';
import { BaseResponse } from '@utils/base-response.domain';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ExpressPurchaseAdapter extends ExpressPurchaseRepository {
    private readonly apiService = inject(RestApiService);

    constructor() { super(); }

    GetAssetConfig(): Observable<BaseResponse<ExpressPurchaseAssetConfig>> {
        return this.apiService.getRequest(
            `${environment.serviceApi}read/v2/wec/digital-product/kv`,
            null
        );
    }

    GetSubscriberType(payload: GetSubscriberTypeDTO): Observable<BaseResponse<SubscriberType>> {
        return this.apiService.postRequest(`${environment.serviceApi}express-purchase/v1/customer/type`, payload);
    }

    GetCategories(payload: GetCategoriesDTO, dlsType?: 'video' | 'games' | 'music'): Observable<BaseResponse<Categories>> {
        let url = `${environment.serviceApi}express-purchase/v1/package/categories`;
        if (dlsType) url += `?dls=${dlsType}`;

        return this.apiService.postRequest(url, payload);
    }

    GetSubCategories(payload: GetSubCategoriesDTO): Observable<BaseResponse<SubCategories>> {
        return this.apiService.postRequest(`${environment.serviceApi}express-purchase/v1/package/sub-categories`, payload);
    }

    GetPackageList(payload: GetPackageListDTO): Observable<BaseResponse<PackageList>> {
        return this.apiService.postRequest(`${environment.serviceApi}express-purchase/v1/package/list`, payload);
    }

    GetBreakOfferPackageList(payload: GetPackageListDTO): Observable<BaseResponse<PackageList>> {
        return this.apiService.postRequest(`${environment.serviceApi}express-purchase/v1/package/offer-break`, payload);
    }

    GetPackageBySubCategory(payload: GetPackageBySubCategoryDTO): Observable<BaseResponse<PackageBySubCategory>> {
        const { page = 1, size = 10, ...body } = payload;
        return this.apiService.postRequest(
            `${environment.serviceApi}express-purchase/v1/package/sub-category?page=${page}&size=${size}`,
            body
        );
    }

    GetCreditList(payload: GetSubscriberTypeDTO): Observable<BaseResponse<CreditList>> {
        return this.apiService.postRequest(`${environment.serviceApi}express-purchase/v1/credit/list`, payload);
    }

    GetRegionList(): Observable<BaseResponse<Regions>> {
      return this.apiService.getRequest(
        `${environment.serviceApi}express-purchase/v1/mapped-regions`,
        null
      );
    }

    GetRoamingType(): Observable<BaseResponse<RoamingType>> {
      return this.apiService.getRequest(
        `${environment.serviceApi}express-purchase/v1/package/roaming-type`,
        null
      );
    }

    GetFilter(): Observable<BaseResponse<FilterSections>> {
        return this.apiService.getRequest(
          `${environment.serviceApi}express-purchase/v1/package/filter-section`,
          null
        );
      }

    GetOfferBreakConfig(): Observable<BaseResponse<OfferBreakConfig>> {
      return this.apiService.getRequest(
        `${environment.serviceApi}express-purchase/v1/offer-break`,
        null
      );
    }
    CheckbillReqOtp(payload: CheckbillDto): Observable<BaseResponse<CheckbillReqOtpRes>> {
        return this.apiService.postRequest(`${environment.serviceApi}express-purchase/v1/bill/otp/request`, payload);
    }

    CheckbillVerifyOtp(payload: CheckbillVerOtpDto): Observable<BaseResponse<null>> {
        return this.apiService.postRequest(`${environment.serviceApi}express-purchase/v1/bill/otp/verify`, payload);
    }

    CheckBill(payload: CheckbillDto, reqHeader: CheckbillAdditionalHeaderItem[]): Observable<BaseResponse<CheckbillRes>> {
        return this.apiService.postRequest(`${environment.serviceApi}express-purchase/v1/bill/check`, payload, reqHeader);
    }

    GetFaq(payload: FaqPayload): Observable<BaseResponse<FaqPackage>> {
      const urlDefault = `express-purchase/v1/faq/${payload.productType}`;
      const urlDls = `express-purchase/v1/dls/faq/${payload.productType}`;

      return this.apiService.getRequest(`${environment.serviceApi}${payload?.dls ? urlDls : urlDefault}`, null);
    }
}
